/* SpawnAnything — player inventory: diamonds, owned crate items, reward log. Pure (no DOM, no three.js).
   Every mutation goes through grant()/spend() (or batch() around several) so a server can later become the
   source of truth: swap the storage adapter for one that syncs, or replace load() with the server snapshot.
   Demo currency only ("Diamanten"), no real money anywhere. */

export const SCHEMA_VERSION = 1;
export const STORAGE_KEY = 'aw-inventory';
export const START_DIAMONDS = 300; // welcome gift on a fresh profile (three crates)
const LOG_MAX = 60;

/* ---------------- storage adapter ---------------- */
/** In-memory key/value store (tests, private mode, blocked storage). */
export function memoryStorage(init = {}) {
  const m = new Map(Object.entries(init));
  return { kind: 'memory', read: k => m.has(k) ? m.get(k) : null, write: (k, v) => { m.set(k, String(v)); return true; }, remove: k => { m.delete(k); } };
}
/** localStorage behind try/catch; falls back to memory once it throws (quota, Safari private, sandboxed iframe). */
export function browserStorage(ls) {
  const mem = memoryStorage();
  let backend = null;
  try { backend = ls === undefined ? globalThis.localStorage : ls; backend?.getItem('__aw_probe'); } catch { backend = null; }
  const fail = () => { backend = null; api.kind = 'memory'; };
  const api = {
    kind: backend ? 'local' : 'memory',
    read(k) { if (backend) { try { return backend.getItem(k); } catch { fail(); } } return mem.read(k); },
    write(k, v) { mem.write(k, v); if (backend) { try { backend.setItem(k, v); return true; } catch { fail(); } } return false; },
    remove(k) { mem.remove(k); try { backend?.removeItem(k); } catch { fail(); } },
  };
  return api;
}

/* ---------------- schema ---------------- */
const isKind = k => k === 'weapon' || k === 'unit' || k === 'tool';
const nonNegInt = v => Number.isFinite(+v) && +v > 0 ? Math.floor(+v) : 0;
export function emptyState(now = Date.now()) {
  return { v: SCHEMA_VERSION, created: now, diamonds: 0, items: {}, rewards: [], daily: { last: '', streak: 0 }, earned: { day: '', battles: 0 }, stats: { crates: 0, battles: 0, wins: 0 } };
}
/** Bring any stored shape up to the current schema. v0 = early prototype `{ diamonds, owned: ['weapon:railgun', …] }`. */
export function migrate(raw, now = Date.now()) {
  if (!raw || typeof raw !== 'object') return null;
  let s = raw;
  if (!s.v) { // v0 -> v1
    const items = {};
    for (const id of Array.isArray(s.owned) ? s.owned : []) {
      const [kind, key] = String(id).split(':');
      if (isKind(kind) && key) items[`${kind}:${key}`] = { kind, key, count: 1, first: now };
    }
    s = { ...emptyState(now), diamonds: nonNegInt(s.diamonds), items };
  }
  // future migrations: if (s.v === 1) s = { ...s, v: 2, … };
  return sanitize(s, now);
}
/** Defensive normalisation of a (migrated) state; unknown extra fields are kept for forward compatibility. */
function sanitize(s, now) {
  const e = emptyState(now), items = {};
  for (const [id, it] of Object.entries(s.items && typeof s.items === 'object' ? s.items : {})) {
    if (!it || !isKind(it.kind) || !it.key || `${it.kind}:${it.key}` !== id) continue;
    const count = nonNegInt(it.count); if (!count) continue;
    items[id] = { kind: it.kind, key: String(it.key), count, first: +it.first || now, ...(it.rarity ? { rarity: String(it.rarity) } : {}) };
  }
  return {
    ...s, v: Math.max(SCHEMA_VERSION, +s.v || 0), created: +s.created || now, diamonds: nonNegInt(s.diamonds), items,
    rewards: Array.isArray(s.rewards) ? s.rewards.filter(r => r && typeof r === 'object').slice(-LOG_MAX) : [],
    daily: { ...e.daily, ...(s.daily || {}) }, earned: { ...e.earned, ...(s.earned || {}) }, stats: { ...e.stats, ...(s.stats || {}) },
  };
}

/* ---------------- store ---------------- */
/**
 * createInventory({ storage, now }) -> inventory
 *   balance, canAfford(n), spend(n, reason), grant(reward), isUnlocked(kind, key), count(kind, key),
 *   items(), rewards(), snapshot(), load(snap), batch(fn), subscribe(fn), meta (daily/earned/stats, read via snapshot)
 * grant(reward): { kind: 'diamonds', amount, reason } or { kind: 'weapon'|'unit'|'tool', key, rarity?, reason }
 */
export function createInventory({ storage = memoryStorage(), now = () => Date.now(), key = STORAGE_KEY, startDiamonds = START_DIAMONDS } = {}) {
  let s = null, depth = 0, dirty = false;
  const subs = new Set();
  try { s = migrate(JSON.parse(storage.read(key) || 'null'), now()); } catch { s = null; }
  const fresh = !s;
  if (fresh) { s = emptyState(now()); if (startDiamonds > 0) { s.diamonds = startDiamonds; log({ kind: 'diamonds', amount: startDiamonds, reason: 'start' }); } }

  function log(r) { s.rewards.push({ t: now(), ...r }); if (s.rewards.length > LOG_MAX) s.rewards.splice(0, s.rewards.length - LOG_MAX); }
  function commit() {
    if (depth > 0) { dirty = true; return; }
    dirty = false;
    try { storage.write(key, JSON.stringify(s)); } catch { /* storage full: keep playing from memory */ }
    for (const fn of subs) { try { fn(inv); } catch (err) { console.error(err); } }
  }
  const inv = {
    get balance() { return s.diamonds; },
    get fresh() { return fresh; },
    canAfford: n => nonNegInt(n) <= s.diamonds,
    /** Take diamonds; false (and no change) when the balance is too low. */
    spend(n, reason = '') {
      n = nonNegInt(n); if (n > s.diamonds) return false;
      if (!n) return true;
      s.diamonds -= n; log({ kind: 'diamonds', amount: -n, reason }); commit(); return true;
    },
    /** Add diamonds or an item. Returns { isNew, count } for items, { amount, balance } for diamonds. */
    grant(r) {
      if (!r || typeof r !== 'object') throw new TypeError('grant: reward object expected');
      if (r.kind === 'diamonds') {
        const n = nonNegInt(r.amount); if (n) { s.diamonds += n; log({ kind: 'diamonds', amount: n, reason: r.reason || '' }); commit(); }
        return { amount: n, balance: s.diamonds };
      }
      if (!isKind(r.kind) || !r.key) throw new TypeError('grant: unknown reward ' + JSON.stringify(r));
      const id = `${r.kind}:${r.key}`, had = s.items[id];
      const it = s.items[id] = had ? { ...had, count: had.count + 1 } : { kind: r.kind, key: String(r.key), count: 1, first: now(), ...(r.rarity ? { rarity: r.rarity } : {}) };
      log({ kind: r.kind, key: r.key, reason: r.reason || '' }); commit();
      return { isNew: !had, count: it.count };
    },
    isUnlocked: (kind, k) => !!s.items[`${kind}:${k}`],
    count: (kind, k) => s.items[`${kind}:${k}`]?.count || 0,
    items: () => Object.values(s.items).map(it => ({ ...it })),
    rewards: () => s.rewards.map(r => ({ ...r })),
    /** Bookkeeping for the economy rules (daily bonus, battle caps, stats). Mutate only through update(). */
    meta: () => structuredClone({ daily: s.daily, earned: s.earned, stats: s.stats }),
    update(fn) { const m = { daily: s.daily, earned: s.earned, stats: s.stats }; fn(m); commit(); },
    /** Group several mutations into one save + one notification (e.g. pay for a crate and receive its item). */
    batch(fn) { depth++; try { return fn(inv); } finally { depth--; if (!depth && dirty) commit(); } },
    snapshot: () => structuredClone(s),
    /** Replace the whole state (e.g. with the server's copy). Invalid input is rejected and changes nothing. */
    load(snap) { const m = migrate(structuredClone(snap), now()); if (!m) return false; s = m; commit(); return true; },
    subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
    storageKind: () => storage.kind || 'custom',
  };
  if (fresh) commit();
  return inv;
}
